import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpen, Quote, Flame } from "lucide-react";
import { knowledgeBase, type LegendRule } from "@/lib/agents/knowledgeBase";

export const Route = createFileRoute("/legends")({
  head: () => ({
    meta: [
      { title: "The Legends Archive — ForexMind AI" },
      {
        name: "description",
        content:
          "The 10 trading minds behind ForexMind AI's 11-Agent Council — from Soros to Simons, every agent embodies a legend.",
      },
      { property: "og:title", content: "The 10 Minds Behind ForexMind AI" },
      {
        property: "og:description",
        content:
          "We didn't invent signals. We resurrected the greatest trading minds in history.",
      },
      { property: "og:url", content: "https://forexmind.ai/legends" },
    ],
    links: [
      { rel: "canonical", href: "https://forexmind.ai/legends" },
    ],
  }),
  component: LegendsPage,
});

interface Legend {
  name: string;
  title: string;
  agent: string;
  famousTrade: string;
  system: string[];
  quote: string;
  book: string;
  amazon: string;
  color: string;
}

const LEGENDS: Legend[] = [
  {
    name: "George Soros",
    title: "The Man Who Broke the Bank of England",
    agent: "The Macro Psychologist",
    famousTrade: "Shorted £10 billion in 1992, made $1B in one day",
    system: ["Reflexivity theory", "Sentiment vs reality gap", "Central bank limit detection"],
    quote:
      "It is not whether you are right or wrong, but how much money you make when you are right.",
    book: "The Alchemy of Finance",
    amazon: "https://www.amazon.com/dp/0471445495",
    color: "#00d4ff",
  },
  {
    name: "Jim Simons",
    title: "The Man Who Solved the Market",
    agent: "The Quant",
    famousTrade: "Medallion Fund: 66% annual returns for 30 years",
    system: ["Pure mathematical edge", "Order flow toxicity", "Statistical regime detection"],
    quote:
      "We are right 50.75% of the time. But we are right 50.75% of the time on a lot of trades.",
    book: "The Man Who Solved the Market",
    amazon: "https://www.amazon.com/dp/073521798X",
    color: "#7F77DD",
  },
  {
    name: "Paul Tudor Jones",
    title: "The Man Who Predicted the 1987 Crash",
    agent: "The Alchemist",
    famousTrade: "Tripled money during 1987 Black Monday crash",
    system: ["Classical chart patterns", "Elliott Wave fractals", "Market top detection"],
    quote: "The secret to success is to always have optionality.",
    book: "Market Wizards",
    amazon: "https://www.amazon.com/dp/0887306101",
    color: "#1D9E75",
  },
  {
    name: "Richard Dennis",
    title: "The Father of Turtle Trading",
    agent: "The Turtle",
    famousTrade: "Turned $400 into $200 million. Proved traders are made not born.",
    system: ["20/55 Donchian breakouts", "Trend following without hesitation", "N-Unit pyramiding"],
    quote:
      "I always say you could publish my trading rules in a newspaper and no one would follow them.",
    book: "Way of the Turtle",
    amazon: "https://www.amazon.com/dp/007148664X",
    color: "#E24B4A",
  },
  {
    name: "Bill Lipschutz",
    title: "The Sultan of Currencies",
    agent: "The Sultan",
    famousTrade: "Made $300 million for Salomon Brothers in forex",
    system: ["Session timing mastery", "Patience over activity", "London/NY overlap focus"],
    quote:
      "If most traders learned to sit on their hands 50% of the time, they would make more money.",
    book: "Market Wizards",
    amazon: "https://www.amazon.com/dp/0887306101",
    color: "#FAC775",
  },
  {
    name: "Bruce Kovner",
    title: "The Greatest Forex Trader Who Ever Lived",
    agent: "The News Trader",
    famousTrade: "Turned $3,000 into billions trading macro events",
    system: ["News reaction analysis", "Macro fundamental bias", "Event volatility protection"],
    quote:
      "Novice traders trade 5 to 10 times too big. They are taking 5 to 10 times too much risk.",
    book: "Market Wizards",
    amazon: "https://www.amazon.com/dp/0887306101",
    color: "#F09595",
  },
  {
    name: "Stanley Druckenmiller",
    title: "30 Years Without a Losing Year",
    agent: "The Asymmetric Hunter",
    famousTrade: "Co-executed the Bank of England trade with Soros",
    system: ["1:3 minimum R:R enforcement", "Bet big when right", "Asymmetric position sizing"],
    quote:
      "It is not whether you are right or wrong, but how much you make when right and lose when wrong.",
    book: "The New Market Wizards",
    amazon: "https://www.amazon.com/dp/0887306675",
    color: "#5DCAA5",
  },
  {
    name: "Ed Seykota",
    title: "The Father of Systematic Trading",
    agent: "The Risk Master",
    famousTrade: "Turned $5,000 into $15,000,000 with systematic rules",
    system: ["Quarter-Kelly position sizing", "ATR-based stops", "Ride winners, cut losers"],
    quote: "Everybody gets what they want from the markets.",
    book: "Market Wizards",
    amazon: "https://www.amazon.com/dp/0887306101",
    color: "#85B7EB",
  },
  {
    name: "Mark Minervini",
    title: "The Contrarian King",
    agent: "The Contrarian",
    famousTrade: "Won US Investing Championship with 155% annual return",
    system: [
      "Fade extreme retail positioning",
      "Crowd psychology reversal",
      "Stop-loss cascade detection",
    ],
    quote:
      "The stock market is the only place where things go on sale and everyone runs out of the store.",
    book: "Trade Like a Stock Market Wizard",
    amazon: "https://www.amazon.com/dp/0071807225",
    color: "#D4537E",
  },
  {
    name: "Peter Brandt",
    title: "45 Years of Classical Chart Mastery",
    agent: "The Scalper",
    famousTrade: "Consistent 40%+ annual returns for 4 decades with classical patterns",
    system: [
      "Fair Value Gap entries",
      "Wick-to-body ratio filtering",
      "Classical pattern precision",
    ],
    quote:
      "The best trade setups are so obvious you almost cannot believe no one else sees it.",
    book: "Diary of a Professional Commodity Trader",
    amazon: "https://www.amazon.com/dp/047052966X",
    color: "#639922",
  },
];

function initials(name: string): string {
  return name
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function LegendsPage() {
  const [viralByLegend, setViralByLegend] = useState<Record<string, LegendRule>>({});

  useEffect(() => {
    knowledgeBase.getTopViralQuotes().then(setViralByLegend).catch(() => {});
  }, []);

  return (
    <main className="min-h-screen bg-background">
      <section className="relative overflow-hidden border-b border-border/60 px-4 py-16 lg:py-24">
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            background:
              "radial-gradient(circle at 30% 20%, #FFD70022, transparent 60%), radial-gradient(circle at 70% 80%, #00d4ff22, transparent 60%)",
          }}
        />
        <div className="relative mx-auto max-w-4xl text-center">
          <div
            className="mb-4 inline-block rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-widest"
            style={{ borderColor: "#FFD700", color: "#FFD700" }}
          >
            The Legends Archive
          </div>
          <h1 className="font-display text-4xl font-bold leading-tight md:text-6xl">
            The 10 Minds Behind <span style={{ color: "#FFD700" }}>ForexMind AI</span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">
            We didn't invent signals. We resurrected the greatest trading minds in history —
            and gave them a council seat in your dashboard.
          </p>
          {/* FIX 4: Agent framework disclaimer */}
          <p className="mx-auto mt-5 max-w-2xl rounded-md border border-border/60 bg-card/40 px-4 py-3 text-xs italic leading-snug text-muted-foreground">
            Agent frameworks are inspired by publicly documented trading philosophies
            and do not imply endorsement, affiliation, or approval by any named individual.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {LEGENDS.map((l) => (
            <LegendCard key={l.name} legend={l} viralQuote={viralByLegend[l.name]} />
          ))}
        </div>
      </section>
    </main>
  );
}

function FireRating({ score }: { score: number }) {
  const flames = Math.max(1, Math.min(5, Math.round(score / 20)));
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[10px]"
      title={`Viral score ${score}/100`}
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <Flame
          key={i}
          className="h-3 w-3"
          style={{
            color: i < flames ? "#FF6B35" : "transparent",
            fill: i < flames ? "#FF6B35" : "transparent",
            stroke: i < flames ? "#FF6B35" : "var(--muted-foreground)",
            opacity: i < flames ? 1 : 0.3,
          }}
        />
      ))}
    </span>
  );
}

function LegendCard({ legend, viralQuote }: { legend: Legend; viralQuote?: LegendRule }) {
  const quoteText = viralQuote?.ruleText || legend.quote;
  const sourceText = viralQuote?.sourceBook || legend.book;
  return (
    <Card
      className="flex flex-col gap-4 border-border/60 bg-card/60 p-5 backdrop-blur"
      style={{ borderTop: `3px solid ${legend.color}` }}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full font-display text-lg font-bold"
          style={{
            backgroundColor: `color-mix(in oklab, ${legend.color} 18%, transparent)`,
            color: legend.color,
            border: `1px solid color-mix(in oklab, ${legend.color} 45%, transparent)`,
          }}
        >
          {initials(legend.name)}
        </div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-bold leading-tight">{legend.name}</h2>
          <p className="text-xs text-muted-foreground">{legend.title}</p>
          <span
            className="mt-1.5 inline-block rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
            style={{
              backgroundColor: `color-mix(in oklab, ${legend.color} 14%, transparent)`,
              color: legend.color,
            }}
          >
            ForexMind Agent · {legend.agent}
          </span>
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
          Famous trade
        </div>
        <p className="mt-1 text-sm">{legend.famousTrade}</p>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
          Signature system
        </div>
        <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
          {legend.system.map((s) => (
            <li key={s} className="flex gap-1.5">
              <span style={{ color: legend.color }}>▸</span>
              <span>{s}</span>
            </li>
          ))}
        </ul>
      </div>

      <blockquote
        className="rounded-md border-l-2 bg-card/40 px-3 py-2 text-xs italic text-muted-foreground"
        style={{ borderColor: legend.color }}
      >
        <div className="mb-1 flex items-center justify-between gap-2">
          <span className="text-[9px] font-bold uppercase tracking-widest not-italic" style={{ color: legend.color }}>
            <Quote className="mr-1 inline h-3 w-3" /> Most-shared rule
          </span>
          {viralQuote ? <FireRating score={viralQuote.viralScore} /> : null}
        </div>
        {quoteText}
        <div className="mt-1 text-[9px] not-italic text-muted-foreground/60">— {sourceText}</div>
      </blockquote>

      <Button
        asChild
        variant="outline"
        size="sm"
        className="mt-auto h-8 justify-start gap-2 text-xs"
      >
        <a href={legend.amazon} target="_blank" rel="noreferrer">
          <BookOpen className="h-3.5 w-3.5" />
          {legend.book} — Read the playbook →
        </a>
      </Button>
    </Card>
  );
}
